import { BehaviorSubject, Subject, timer } from 'rxjs';
import get from 'lodash-es/get';
import { ExportTaskProcessor } from './processors/export-task-processor';
import { ExportContext, ExportTaskCreationOptions } from './export-task.interfaces';
import { ExportFileFormat, ExportTaskStatus } from './export-task.enum';
import { ExportStoreService } from '../../export-overlay.store';
import { JSONGenerator } from './generators/json-generator';
import { ExcelGenerator } from './generators/excel-generator';
import { CSVGenerator } from './generators/csv-generator';
import { ExportTaskInternalShared } from './export-task-internal.interfaces';

const fileFormatsForDownload = {
  [ExportFileFormat.Excel]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  [ExportFileFormat.CSV]: 'text/csv;charset=utf-8',
  [ExportFileFormat.JSON]: 'application/json;charset=UTF-8'
}

export class ExportTask {
  public readonly uuid = crypto.randomUUID();
  public readonly id: string;
  private internalTitle: string;
  private startFn: (context: ExportContext) => void;
  private nextFn: (context: ExportContext) => void;
  private processor: ExportTaskProcessor;
  private format: ExportFileFormat;
  private status$ = new BehaviorSubject(ExportTaskStatus.Busy);
  private stopped$ = new Subject<void>();
  private context: ExportContext;
  private internalShared: ExportTaskInternalShared;

  get title() {
    return this.internalTitle;
  }

  get status() {
    return this.status$.value;
  }

  get iterations() {
    return this.context.totalIterations;
  }

  get iteration() {
    return this.context.iteration;
  }

  constructor(
    private store: ExportStoreService,
    options: ExportTaskCreationOptions,
  ) {
    this.id = get(options, 'id');
    this.internalTitle = get(options, 'title', this.id);
    this.startFn = get(options, 'start', () => {});
    this.nextFn = get(options, 'next', () => {});
    this.format = get(options, 'format');
    this.processor = get(options, 'processor', new ExportTaskProcessor());

    this.context = {
      iteration: 0,
      totalIterations: get(options, 'iterations', 1),
      data: get(options, 'data', {}),
      processor: this.processor,
      status: ExportTaskStatus.Busy,
      statusChanges: this.status$.asObservable(),
      stopped: this.stopped$.asObservable(),
      add: (data: any) => this.addData(data),
      next: () => {
        this.context.iteration += 1;
        this.goToNextIteration();
      },
    },
    this.internalShared = {
      id: this.id,
      setTitle: (titleChange: string) => {
        this.internalTitle = titleChange;
      },
      getTitle: () => {
        return this.internalTitle;
      },
      setIterations: (value: number) => {
        this.context.totalIterations = value;
      },
      setIteration: (value: number) => {
        this.context.iteration = value;
      },
      getIteration: () => {
        return this.context.iteration;
      },
      isStopped: () => {
        return this.status !== ExportTaskStatus.Busy;
      }
    };
  }

  private downloadFile(filename: string, content: any, format: ExportFileFormat) {
    if (this.status !== ExportTaskStatus.Busy) return;
    const a = document.createElement('a');
    const blob = new Blob([content], { type: get(fileFormatsForDownload, format) });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  private generateJSONExportContent() {
    return new JSONGenerator(this.processor.sections).generate();
  }

  private async generateExcelExportContent() {
    const generator = new ExcelGenerator(this.processor.sections, this.store, this.internalShared);
    const wb = await generator.generateWorkBook();
    return wb.xlsx.writeBuffer();
  }

  private generateCSVExportContent() {
    const generator = new CSVGenerator(this.processor.sections);
    return generator.generateDocuments();
  }

  private async generateAndDownloadFiles() {
    if (this.format === ExportFileFormat.Excel) {
      const excelContent = await this.generateExcelExportContent();
      this.downloadFile(`Export_${this.id}.xlsx`, excelContent, this.format);
    } else if (this.format === ExportFileFormat.CSV) {
      const documents = this.generateCSVExportContent();
      documents.forEach((doc) => {
        const titleParts = ['Export', this.id];
        if (doc.title && documents.length > 1) titleParts.push(doc.title);
        this.downloadFile(`${titleParts.join('_')}.csv`, '\ufeff' + doc.content, this.format);
      });
    } else {
      this.downloadFile(`Export_${this.id}.json`, this.generateJSONExportContent(), this.format);
    }
  }

  private initProcessor() {
    this.processor.__init({
      title: this.title,
      data: this.context.data,
      format: this.format,
    });
  }

  private addData(data?: any) {
    if (typeof data !== 'undefined' && data !== null) {
      this.processor.onAddData(data);
    }
  }

  private goToNextIteration() {
    if (this.status !== ExportTaskStatus.Busy || this.context.iteration === 0) return;

    const ctx = { data: this.context.data };
    Object.defineProperty(ctx, 'processor', { get: () => this.context.processor });
    Object.defineProperty(ctx, 'status', { get: () => this.status });
    Object.defineProperty(ctx, 'statusChange', { get: () => this.status$.asObservable() });
    Object.defineProperty(ctx, 'stopped', { get: () => this.context.stopped });
    Object.defineProperty(ctx, 'iteration', { get: () => this.context.iteration });
    Object.defineProperty(ctx, 'totalIterations', { get: () => this.context.totalIterations });
    Object.defineProperty(ctx, 'add', { get: () => this.context.add });
    Object.defineProperty(ctx, 'next', { get: () => this.context.next });

    if (this.context.iteration > this.context.totalIterations) {
      this.complete();
    } else {
      this.nextFn(ctx as ExportContext);
      this.store.emitChange();
    }
  }

  private async complete() {
    if (this.status !== ExportTaskStatus.Busy) return;
    await this.generateAndDownloadFiles();
    this.status$.next(ExportTaskStatus.Complete);
    this.stopped$.next();
    this.store.emitChange();
    this.store.setVisible(true);
    timer(4500).subscribe(() => { this.store.removeTask(this.id); });
  }

  start() {
    if (this.status !== ExportTaskStatus.Busy) return;
    this.initProcessor();
    this.store.addTask(this);
    this.startFn(this.context);
    this.context.iteration = 1;
    this.goToNextIteration();
  }

  cancel() {
    this.status$.next(ExportTaskStatus.Canceled);
    this.stopped$.next();
    this.store.emitChange();
    timer(4500).subscribe(() => { this.store.removeTask(this.id); });
  }
}
